import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ContentService, ServicePathProviderService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { BlendPortfolioUtil, HoldingTableDetails, PortfolioFlatteningUtil } from '@ddt-web/blending';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AuthUtil, OktaAuthenticationService } from '@ddt-web/core';
import { AppStoreConfig } from '@ddt-web/data-service';
import { DialogIcon, TreeNodeMap, WebUserDataConstants } from '@ddt-web/domain';
import { StoreService } from '@ddt-web/store-service';
import { UrlRedirectService, UrlUtils } from '@ddt-web/url';
import { PartnerConstants, PartnerUserProfileService } from '@ddt-web/user-data';
import { WebToolsPlatformDataService } from '@ddt-web/web-tools-platform';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Update } from '@ngrx/entity';
import { chain } from 'lodash';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as omit from 'lodash/omit';
import * as omitBy from 'lodash/omitBy';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { PartnerPortfolioUtil } from '../../alerts/utils/partner-util';
import { PartnerDataService, PartnerPortfolioDataService } from '../../data-service';

@UntilDestroy()
@Component({
	selector: 'pi-export-model-modal',
	templateUrl: './export-model-modal.component.html',
	styleUrls: ['./export-model-modal.component.scss']
})
export class ExportModelModalComponent implements OnInit {
	@Input() public paStoreConfig: AppStoreConfig;
	@Input() public mode: string;
	@Input() public portfolioId: string;
	@Input() public baseModelType: string;

	@Output() public isVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

	@ViewChild(NgForm) public exportForm: NgForm;

	public isShown: boolean = true;
	public isSaving: boolean = false;
	public isSaved: boolean = false;

	public validationMessages: ValidationMessage[] = [];
	public isModelViewOnly: boolean = false;

	public baseModelName: string;
	public platformName: string;
	public platformIdentifier: string;

	public initialPortfolioName: string;
	public portfolioNameUpdate: string;
	public shouldRenamePortfolio: boolean = false;
	public isPortfolioNameChanged: boolean = false; // flag to track if portfolio name is changed
	public isPortfolioNameInvalid: boolean = false; // flag to track if the portfolio name is invalid (either needs to be changed or updating the name failed)

	public shouldFlattenPortfolio: boolean = false;

	public updatedPortfolio: any;
	private portfolioFromStore: any;
	private validChildrenHoldings: HoldingTableDetails[] = [];

	public isPortfolioUpdateLoading: boolean = false;
	public isPortfolioUpdateFailing: boolean = false;
	private isPortfolioCreatedInWtp: boolean = false;

	// risk scale and program handle
	public modelType: string = '';
	public targetRisk: string = '';
	private riskScaleListOptions$: Observable<any[]>;
	private programHandleListOptions$: Observable<any[]>;

	public imagePath: string;
	private trackMessage: string = '';

	constructor(
		private contentService: ContentService,
		private storeService: StoreService,
		private urlRedirectService: UrlRedirectService,
		private partnerPortfolioDataService: PartnerPortfolioDataService,
		private partnerUserProfileService: PartnerUserProfileService,
		private webToolsPlatformDataService: WebToolsPlatformDataService,
		private servicePathProvider: ServicePathProviderService,
		private partnerDataService: PartnerDataService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private oktaAuthenticationService: OktaAuthenticationService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		@Inject('environment') private environment: any
	) { }

	public ngOnInit(): void {

		this.storeService
			.selectAll(this.paStoreConfig.associatedPortfolios)
			.pipe(untilDestroyed(this))
			.subscribe((portfolios: any) => {
				const portfolio = get(portfolios, `entities[${this.portfolioId}]`);

				if (portfolio) {
					this.setUpPortfolioDetails(portfolio);
					this.setUpPortfolioValidity(portfolio, portfolios);
				}
			});

		if (this.mode === 'editOriginal') {
			this.trackMessage = TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_EDIT_CTA;
		} else {
			this.trackMessage = TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_CREATE_CTA;
		}

		this.partnerDataService.getPartnerProgramAndRiskConfig(this.paStoreConfig);
		this.riskScaleListOptions$ = this.partnerDataService.getRiskScaleListOptions$();

		// filter out APM option if portfolioFromStore has a model child
		this.programHandleListOptions$ = this.partnerDataService.getProgramHandleListOptions$()
			.pipe(map(options => {
				const hasModelChild = get(this.portfolioFromStore, 'children', []).some(child => get(child, 'identifierType') === WebUserDataConstants.IDENTIFIER_TYPE.MODEL_ID);

				return options.filter(option => {
					if (hasModelChild) {
						return option.programTypeId !== +this.contentService.get('PROGRAM_HANDLE_APM_PROGRAM_TYPE_ID', PartnerConstants.PROGRAM_HANDLE_APM_PROGRAM_TYPE_ID);
					} else {
						return true;
					}
				});
			}));
	}

	public setUpPortfolioDetails(portfolioFromStore: any): void {
		this.platformName = get(portfolioFromStore, 'baseModelType', this.baseModelType);
		this.baseModelName = get(portfolioFromStore, 'baseModelName');

		// set store and updated portfolio to portfolioFromStore
		this.portfolioFromStore = cloneDeep(portfolioFromStore);
		this.updatedPortfolio = cloneDeep(portfolioFromStore);

		// set the initialPortfolioName and portfolioNameUpdate to the portfolio name in the store
		this.initialPortfolioName = portfolioFromStore.description;
		this.portfolioNameUpdate = this.initialPortfolioName;
	}

	public setUpPortfolioValidity(portfolioFromStore: any, portfolios: TreeNodeMap): void {

		// if WTP portfolio already belongs to a partner portfolio, set showRenamePortfolio to true to force user to rename portfolios when mode is create
		this.shouldRenamePortfolio = !isNil(get(portfolioFromStore, 'otherIdentifiers')) && this.mode === PartnerConstants.EXPORT_ACTIONS.CREATE;
		this.checkPortfolioNameValidity();

		// update the flag to add the warning disclaimer for flattening
		this.shouldFlattenPortfolio = get(portfolioFromStore, 'children', []).some(child => BlendPortfolioUtil.isUserPortfolio(child));

		// set valid children from portfolio (including flattened if needed)
		PortfolioFlatteningUtil.transformNodeMapToHoldings(portfolios, portfolioFromStore, 100, this.validChildrenHoldings, false, 'children', [], 0, false);
	}

	public onPortfolioNameChange(updatedName: string): void {
		this.isPortfolioNameChanged = this.initialPortfolioName !== updatedName;
		this.portfolioNameUpdate = updatedName;

		this.checkPortfolioNameValidity();
	}

	public onProgramHandleChange(event: any): void {
		if (event && event.target) {
			this.modelType = event.target.value;
			const programHandleOptions = event.target.options;
			const selectedProgramHandle = programHandleOptions[programHandleOptions.selectedIndex].text;
			const analyticsEvent = new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.MODEL_TYPE_CHANGE + selectedProgramHandle, {});
			this.tealiumAnalyticsService.trackEventTealium(analyticsEvent);
		}
	}

	public onRiskHandleChange(event: any): void {
		if (event && event.target) {
			this.targetRisk = event.target.value;
			const riskHandleOptions = event.target.options;
			const selectedRiskHandle = riskHandleOptions[riskHandleOptions.selectedIndex].text;
			const analyticsEvent = new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.RISK_CATEGORY_CHANGE + selectedRiskHandle, {});
			this.tealiumAnalyticsService.trackEventTealium(analyticsEvent);
		}
	}

	public checkPortfolioNameValidity(): void {
		// mark portfolio as invalid if portfolio should be renamed and current name has not been changed
		this.isPortfolioNameInvalid = this.shouldRenamePortfolio && !this.isPortfolioNameChanged;
	}

	public isSendToPlatformDisabled(): boolean {
		const isDisabledForCreateNewMode = (!(this.exportForm && this.exportForm.valid) || !this.modelType || !this.targetRisk) && this.mode !== 'editOriginal';
		const isDisabledForUpdatePortfolio = this.isPortfolioUpdateLoading || this.isPortfolioNameInvalid || this.isPortfolioUpdateFailing;

		return isDisabledForCreateNewMode || isDisabledForUpdatePortfolio;
	}

	public sendToPlatform(): void {

		// reset validation messages - if user retries to send to platform
		this.validationMessages = [];

		// update WTP object before sending to platform (for flattening portfolio or creating new one) - WTP portfolio json should match the one sent to platform
		this.updateWtpPortfolio(this.updatedPortfolio).subscribe(
			isUpdateCompleted => {
				this.isSaving = false; // hide loading spinner

				if (isUpdateCompleted) {
					let portfolioData = cloneDeep(this.updatedPortfolio);

					// remove unnecessary fields, smaller payload this way, otherwise iRetire returns 500 error
					const unimportantFields = ['allData', 'perms'];
					portfolioData = omit(portfolioData, unimportantFields);

					this.isSaving = true; // show loading spinner for createUpdate call
					this.isShown = false; // hide 'Send to Platform' dialog

					let externalModelId;
					let portfolioJsonData;

					switch (this.mode) {
						case PartnerConstants.EXPORT_ACTIONS.EDIT:
							externalModelId = PartnerPortfolioUtil.getPartnerPortfolioId(get(portfolioData, 'baseModel'));
							portfolioData.description = this.baseModelName;
							portfolioJsonData = { data: [portfolioData] };
							break;
						case PartnerConstants.EXPORT_ACTIONS.CREATE:
							if (this.modelType && this.targetRisk) {
								portfolioData.originPlatform = { programHandle: this.modelType.toUpperCase(), riskHandle: this.targetRisk.toUpperCase() };
							}
							portfolioJsonData = { data: [portfolioData] };
							break;
						default:
							break;
					}

					this.partnerPortfolioDataService.createUpdateExternalModel(
						this.platformName.toLowerCase(),
						this.portfolioId, JSON.stringify(portfolioJsonData), externalModelId
					).subscribe(
						response => {

							if (response.success && isEmpty(response.validationAlerts)) {
								this.platformIdentifier = response.platformIdentifier;
								this.imagePath = DialogIcon.CHECK_MARK;
								this.isSaving = false;
								this.isSaved = true;
								this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_SUCCESS, {}));

								if (this.mode === PartnerConstants.EXPORT_ACTIONS.CREATE) {
									this.updatedPortfolio = PartnerPortfolioUtil.updateOtherIdentifiersToPortfolio(
										this.updatedPortfolio,
										this.platformName,
										this.platformIdentifier
									);
								}

							} else if (response.success && !isEmpty(response.validationAlerts)) {
								this.handleValidationAlertAndReturnIcon(response).subscribe(validationIcon => {
									this.imagePath = validationIcon;
									this.isSaving = false;
									this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_FAIL, {}));
								});

							} else {
								this.imagePath = DialogIcon.ALERT_MARK;
								this.validationMessages = [new ValidationMessage(MSG_TYPE.ALERT, 'EXPORT_ALERT_MESSAGE')];
								this.isSaving = false;
								this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_FAIL, {}));
							}

						},
						error => {
							this.handleError(error);
						}
					);
				}
			},
			error => {
				this.isShown = false; // hide 'Send to platform' dialog
				this.handleWtpCallError(error);
			});
	}

	public retry(): void {
		this.isSaving = true; // show loading spinner
		this.sendToPlatform();
	}

	public editPortfolio(): void {
		// using updatedPortfolio uniqueId - covers the case when a new portfolio was created or portfolio was edited
		this.updatePortfolioInStore();
		this.urlRedirectService.redirectToPortfolioEditorView({ mode: 'EDIT', portfolioIds: this.updatedPortfolio.uniqueId });
	}

	public close(): void {
		if (this.isPortfolioCreatedInWtp) {
			// redirect to overview page with new portfolio id
			this.urlRedirectService.redirectToOverviewTab([this.updatedPortfolio.uniqueId]);
		} else {
			this.updatePortfolioInStore();
			this.isVisibleChange.emit(false);
		}
	}

	public hasAlert(messages: ValidationMessage[]): boolean {
		return messages.some(val => val.type === MSG_TYPE.ALERT);
	}

	public showInvalidSecuritiesInModal(message: ValidationMessage): boolean {
		return message.securities && !message.securities.every(security => security === 'N/A');
	}

	public goToPlatform(): void {
		this.partnerUserProfileService.getPartner()
			.pipe(first())
			.subscribe(partnerName => {
				const origin: Window = window.opener;

				const partnerSsoRedirectPath = `/iretire-server/iretire/${partnerName}/sso?message=model_positions:`;
				const ssoRedirectUrl = AuthUtil.isOktaEnabled(this.environment.cloud) ?
					this.getCloudServicePath(partnerName) + this.platformIdentifier :
					this.servicePathProvider.getServicePath('api-gateway') + partnerSsoRedirectPath + this.platformIdentifier;

				this.openModelInPlatform(origin, ssoRedirectUrl);
				this.close();
			});
	}

	private getCloudServicePath(clientName: string): string {
		const env = UrlUtils.isProduction() ? 'www' : 'testc';
		const url = `https://${env}.blackrock.com/tools-m/services/v1/${clientName}/sso?message=model_positions:`;
		return url;
	}

	/**
	 * Checks if platform window exists and it is not closed and
	 * - updates the location href to the ssoRedirectUrl if true
	 * - otherwise it opens a new window with the ssoRedirectUrl
	 *
	 * ssoRedirectUrl is used to redirect the user to the platform with the correct platform identifier
	 * (model that was sent back successfully).
	 *
	 * @param platformWindow
	 * @param ssoRedirectUrl
	 */
	public openModelInPlatform(platformWindow: Window, ssoRedirectUrl: string): void {
		this.isSaving = true;

		if (AuthUtil.isOktaEnabled(this.environment.cloud)) {
			const existingForm = document.querySelector('#proposalForm');
			const form = existingForm as HTMLFormElement || document.createElement('form');
			form.setAttribute('action', ssoRedirectUrl);
			form.setAttribute('target', '_blank');
			form.setAttribute('method', 'POST');
			if (!existingForm) {
				AuthUtil.addInputsToForm(form, [
					{
						name: 'userToken',
						value: this.oktaAuthenticationService.getAccessToken()
					},
					{
						name: 'x-api-key',
						value: this.oktaAuthenticationService.getApiKey()
					}
				]);
				form.setAttribute('id', 'proposalForm');
				document.body.appendChild(form);
			}

			form.submit();
		} else {
			if (platformWindow && !platformWindow.closed) {
				platformWindow.focus();
				platformWindow.location.href = ssoRedirectUrl;
			} else {
				platformWindow = window.open(ssoRedirectUrl, '_blank');
				platformWindow.focus();
			}
		}

		const envestTimeout = this.contentService.get('ENVESTNET_GOTO_TIMEOUT_MS', '6000');
		setTimeout(() => this.isSaving = false, Number(envestTimeout));
	}

	/**
	 * Returns the track message for an action (send to platform or cancel) that is going to be used for adobe/tealium analytics
	 */
	public getTrackMessage(action: string): string {
		return this.trackMessage + action;
	}

	/**
	 * Handles validation alert and returns an observable of icon.
	 *
	 * If response has invalid securities:
	 * - edit WTP object with portfolioJsonWithInvalidSecurities
	 * - if edit is successful, set validationMessages and return DialogIcon.EXCLAMATION_TRIANGLE
	 * - if edit is not successful, handleWtpCallError is called and return DialogIcon.ALERT_MARK
	 *
	 * If response has MODEL_NOT_EDITABLE validation alert, set validationMessages and return DialogIcon.EXCLAMATION_TRIANGLE
	 *
	 * Otherwise, set validationMessages with EXPORT_ALERT_MESSAGE and return DialogIcon.ALERT_MARK.
	 *
	 * @param response
	 */
	private handleValidationAlertAndReturnIcon(response): Observable<string> {

		const validationMessages = chain(response.validationAlerts)
			.filter(alert => {
				// Currently only handling 'not eligible', 'invalid' securities and 'model.not.editable' - anything else triggers the default 'something went wrong' error
				return (
					alert.code === PartnerConstants.ENVESTNET_VALIDATION.SECURITY_NOT_ELIGIBLE ||
					alert.code === PartnerConstants.ENVESTNET_VALIDATION.SECURITY_NOT_FOUND ||
					alert.code === PartnerConstants.ENVESTNET_VALIDATION.CUSIP_NOT_FOUND ||
					alert.code === PartnerConstants.ENVESTNET_VALIDATION.MODEL_NOT_EDITABLE
				);
			})
			.map(alert => ({
				// remap similar errors to the same code
				...alert,
				code:
					alert.code === PartnerConstants.ENVESTNET_VALIDATION.SECURITY_NOT_FOUND || alert.code === PartnerConstants.ENVESTNET_VALIDATION.CUSIP_NOT_FOUND
						? PartnerConstants.ENVESTNET_VALIDATION.SECURITY_NOT_FOUND
						: alert.code
			}))
			.groupBy(alert => alert.code)
			.map((alerts, code) => new ValidationMessage(MSG_TYPE.WARNING, code, alerts.map(alert => get(alert, 'violatingObject.value.ticker', 'N/A'))))
			.value();

		const invalidSecurities = chain(validationMessages) // map out securities with issues
			.filter(valMessage => valMessage.code !== PartnerConstants.ENVESTNET_VALIDATION.MODEL_NOT_EDITABLE) // no invalid securities if model is not editable
			.flatMap(message => message.securities)
			.value();

		if (invalidSecurities.length) {
			// if portfolio has invalid securities, create portfolioJsonWithInvalidSecurities with originNotTradableSecurities
			const portfolioJsonWithInvalidSecurities = {
				...this.updatedPortfolio,
				originNotTradableSecurities: invalidSecurities
			};

			// edit WTP object with portfolioJsonWithInvalidSecurities and return icon based on the succes of edit
			return this.editWtpPortfolio(portfolioJsonWithInvalidSecurities).pipe(
				map(response => {
					this.validationMessages = validationMessages;
					return DialogIcon.EXCLAMATION_TRIANGLE;
				}),
				catchError(error => {
					this.handleWtpCallError(error);
					return of(DialogIcon.ALERT_MARK);
				})
			);

		} else if (this.validationMessages.find(valMessage => valMessage.code === PartnerConstants.ENVESTNET_VALIDATION.MODEL_NOT_EDITABLE)) {
			this.isModelViewOnly = true;
			this.validationMessages = validationMessages;

			return of(DialogIcon.EXCLAMATION_TRIANGLE);

		} else {
			this.validationMessages = [new ValidationMessage(MSG_TYPE.ALERT, 'EXPORT_ALERT_MESSAGE')];

			return of(DialogIcon.ALERT_MARK);
		}
	}

	private updatePortfolioInStore(): void {
		const update: Update<any> = {
			id: this.portfolioId,
			changes: this.updatedPortfolio
		};

		// Update the associatedPortfolios store with the new values of the portfolio
		this.storeService.updateOne(this.storeConfig.associatedPortfolios, update);
	}

	/**
	 * If isPortfolioNameChanged:
	 * - WTP search to check new portfolio name is valid
	 * - if portfolio name already exists, isPortfolioNameInvalid is set to true and 'Send to Platform' dialog is opened to show the error and force user to rename portfolio
	 * - if portfolio name is valid (does not exist), createWtpPortfolio is called
	 *
	 * If shouldFlattenPortfolio:
	 * - hide 'Send to Platform' dialog
	 * - show loading spinner
	 * - editWtpPortfolio is called
	 *
	 * If portfolio name is not changed and portfolio should not be flatten, observable of true is returned - no action needed.
	 *
	 * @param portfolioToUpdateJson  json of the portfolio to be updated in WTP (if necessary)
	 */
	private updateWtpPortfolio(portfolioToUpdateJson: any): Observable<boolean> {

		if (this.isPortfolioNameChanged) {
			this.updateWtpCallStatus(true, false);

			// check if there is other portfolio with the same name
			return this.webToolsPlatformDataService.searchPortfolios(this.portfolioNameUpdate)
				.pipe(first(),
					switchMap(portfolioJson => {

						if (portfolioJson.length) {
							this.updateWtpCallStatus(false, false);

							// if there already is a portfolio with this name, set the isPortfolioNameInvalid to true
							this.isPortfolioNameInvalid = true;

							// make sure the 'Send to Platform' dialog is open to show the invalid portfolio name error
							this.isShown = true;

							return of(false);
						} else {

							// if portfolio name is valid, create new WTP portfolio
							return this.createWtpPortfolio(portfolioToUpdateJson);
						}
					}));

		} else if (this.shouldFlattenPortfolio) {

			this.isSaving = true; // show loading spinner
			this.isShown = false; // hide 'Send to Platform' dialog

			// if portfolio should be flatten, edit WTP portfolio
			return this.editWtpPortfolio(portfolioToUpdateJson);

		} else {
			// return true if no WTP update is needed
			return of(true);
		}
	}

	/**
	 * Creates portfolio in WTP with the provided json.
	 *
	 * If the create portfolio call is successful:
	 * - updatedPortfolio is set to the newWtpPortfolio
	 * - isPortfolioCreatedInWtp is set to true - used for close actions
	 * - isPortfolioNameChanged is set to false
	 * - an observable of true is returned
	 *
	 * If the create portfolio call returns an object with no uniqueId set, handleWtpCallError is called.
	 *
	 * @param portfolioToUpdateJson json of the portfolio to edit in WTP
	 */
	private createWtpPortfolio(portfolioToUpdateJson: any): Observable<boolean> {

		this.updateWtpCallStatus(true, false);

		// create json of new portfolio
		const newPortfolioJson = this.getPortfolioJsonToUpdate(portfolioToUpdateJson, true);

		return this.webToolsPlatformDataService.savePortfolioToAPI(newPortfolioJson)
			.pipe(first(),
				map(response => {
					const newWtpPortfolio = get(response, 'resultMap.SEARCH_EDIT_RESULTS[0].resultList[0]');

					if (!get(newWtpPortfolio, 'uniqueId')) {
						this.isShown = false; // hide 'Send to Platform' dialog
						this.handleWtpCallError('Could not create new portfolio with name: ' + this.portfolioNameUpdate);

						return false;
					} else {
						this.updateWtpCallStatus(false, false);

						this.isPortfolioCreatedInWtp = true;
						this.isPortfolioNameChanged = false;

						// set the updatedPortfolio to the new WTP portfolio
						this.updatedPortfolio = cloneDeep(newWtpPortfolio);

						return true;
					}
				}));
	}

	/**
	 * Edits portfolio in WTP with the provided json.
	 *
	 * If the portfolio edit call is successful:
	 * - updatedPortfolio is set to the editedPortfolioJson
	 * - shouldFlattenPortfolio is updated
	 * - an observable of true is returned
	 *
	 * @param portfolioToUpdateJson json of the portfolio to edit in WTP
	 */
	private editWtpPortfolio(portfolioToUpdateJson: any): Observable<boolean> {

		this.updateWtpCallStatus(true, false);

		// create json of edited portfolio
		const editedPortfolioJson = this.getPortfolioJsonToUpdate(portfolioToUpdateJson);

		return this.webToolsPlatformDataService
			.editObject(editedPortfolioJson)
			.pipe(
				first(),
				map(response => {
					const editedWtpPortfolio = get(response, 'resultMap.SEARCH_EDIT_RESULTS[0].resultList[0]');

					if (get(editedWtpPortfolio, 'uniqueId')) {
						this.updateWtpCallStatus(false, false);

						// set the updatedPortfolio to the edited WTP portfolio
						this.updatedPortfolio = cloneDeep(editedWtpPortfolio);

						// update shouldFlattenPortfolio so edit call won't be made again when retry
						this.shouldFlattenPortfolio = get(editedWtpPortfolio, 'children', []).some(child => BlendPortfolioUtil.isUserPortfolio(child));

						return true;
					} else {
						this.isShown = false; // hide 'Send to Platform' dialog
						this.handleWtpCallError('Could not create new portfolio with name: ' + this.portfolioNameUpdate);

						return false;
					}
				})
			);
	}

	/**
	 * Builds portfolio json to update (create or edit WTP call) based on the provided portfolio.
	 *
	 * If portfolio json is used for create API call:
	 * - portfolio name is updated with the portfolioNameUpdate
	 * - uniqueId, id, flags and otherIdentifiers are removed
	 *
	 * If portfolio json is used for edit API call:
	 * - flags are updated
	 *
	 * In both cases (create new or edit):
	 * - children are updated with the validChildrenHoldings (valid flatten children)
	 *
	 * @param portfolio portfolio json to be updated
	 * @param isUsedForCreateNewCall true if json is used for create new portfolio, false otherwise
	 */
	private getPortfolioJsonToUpdate(portfolio: any, isUsedForCreateNewCall: boolean = false): Object {
		const updatedFlags = Object.assign({ ...portfolio.flags } || {}, { containsChildrenPortfolios: BlendPortfolioUtil.isBlend(portfolio) });

		let portfolioJsonToUpdate: Object = {
			...portfolio,
			description: isUsedForCreateNewCall ? this.portfolioNameUpdate : portfolio.description,
			uniqueId: isUsedForCreateNewCall ? undefined : portfolio.uniqueId,
			id: isUsedForCreateNewCall ? undefined : portfolio.id,
			otherIdentifiers: isUsedForCreateNewCall ? undefined : portfolio.otherIdentifiers,
			originNotTradableSecurities: isUsedForCreateNewCall ? [] : portfolio.originNotTradableSecurities,
			flags: isUsedForCreateNewCall ? {} : updatedFlags,
			children: this.validChildrenHoldings
		};

		portfolioJsonToUpdate = omitBy(portfolioJsonToUpdate, value => isNil(value));
		return portfolioJsonToUpdate;
	}

	private updateWtpCallStatus(isLoading: boolean, isFailing: boolean): void {
		this.isPortfolioUpdateLoading = isLoading;
		this.isPortfolioUpdateFailing = isFailing;
	}

	private handleError(error: any): void {
		this.isSaving = false; // hide loading spinner
		this.imagePath = DialogIcon.ALERT_MARK;
		this.validationMessages = [new ValidationMessage(MSG_TYPE.ALERT, 'EXPORT_ALERT_MESSAGE')];
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.PORTFOLIO_EXPORT_FAIL, {}));
		console.error(error);
	}

	private handleWtpCallError(error: string): void {
		this.updateWtpCallStatus(false, true);
		this.handleError(error);
	}

}

enum MSG_TYPE {
	WARNING,
	ALERT
}

class ValidationMessage {
	constructor(public readonly type: MSG_TYPE, public readonly code: string, public readonly securities: string[] = []) { }
}
